import { Fragment, ReactNode } from 'react';
import cssVars from '@figma/fpl-tokens/css-vars';
import { Link as FplLink } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { TextSmRg, TextLgSb } from '@/UI/AppTypography/AppTypography';
import { FlexCenHorVer, FlexColCenHorVer } from '@/UI/styles';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import type { SxProps } from '@/UI/theme.exports';

type ActionObject = { label: string; onClick: () => void };
type ActionObjectWithLink = { label: string; href: string; target?: string };

interface EmptyStateProps {
  icon?: ReactNode;
  title?: string | ReactNode;
  description: string | ReactNode;
  actions: Array<React.ReactNode | ActionObject | ActionObjectWithLink>;
  sx?: SxProps;
}

const isNotEmpty = (
  action: ReactNode | ActionObject | ActionObjectWithLink,
): action is Exclude<ReactNode, null | undefined> | ActionObject | ActionObjectWithLink => {
  return !!action;
};

const isActionObject = (action: ReactNode | ActionObject | ActionObjectWithLink): action is ActionObject => {
  return !!action && typeof action === 'object' && 'label' in action && 'onClick' in action;
};

const isActionObjectWithLink = (action: ReactNode | ActionObjectWithLink): action is ActionObjectWithLink => {
  return !!action && typeof action === 'object' && 'label' in action && 'href' in action;
};

export const EmptyState = ({ icon, title, description, actions, sx }: EmptyStateProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();
  const filteredActions = actions.filter(isNotEmpty);

  return (
    <FlexCenHorVer
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: isFplMigrationEnabled ? cssVars.color.bg : color.Black100,
        ...sx,
      }}
    >
      <FlexColCenHorVer sx={{ gap: 4, width: '450px' }}>
        <FlexColCenHorVer sx={{ gap: 2, width: '100%' }}>
          {icon && (
            <Box
              sx={{
                color: token.text,
                height: '42px',
                width: '42px',
                '& > svg': { height: '42px', width: '42px' },
              }}
            >
              {icon}
            </Box>
          )}
          <FlexColCenHorVer sx={{ gap: 0.5, width: '100%' }}>
            {title && (
              <TextLgSb component="div" sx={{ textAlign: 'center' }}>
                {title}
              </TextLgSb>
            )}
            <TextSmRg component="div" sx={{ color: token.textSecondary, textAlign: 'center' }}>
              {description}
            </TextSmRg>
          </FlexColCenHorVer>
        </FlexColCenHorVer>
        <FlexCenHorVer sx={{ gap: 1, width: '100%' }}>
          {filteredActions.map(
            (action: Exclude<ReactNode, null | undefined> | ActionObject | ActionObjectWithLink, index) =>
              isActionObject(action) ? (
                <ButtonContained key={index} mode="outlined" size="small" onClick={action.onClick}>
                  {action.label}
                </ButtonContained>
              ) : isActionObjectWithLink(action) ? (
                <FplLink.Button key={index} href={action.href} newTab={action.target === '_blank'} variant="secondary">
                  {action.label}
                </FplLink.Button>
              ) : (
                <Fragment key={index}>{action}</Fragment>
              ),
          )}
        </FlexCenHorVer>
      </FlexColCenHorVer>
    </FlexCenHorVer>
  );
};
